import { Head, router, usePage } from "@inertiajs/react";
import { useEffect, useState } from "react";
import { Check, CheckCircle2, Copy, Users } from "lucide-react";
import SchoolLayout from "@/components/schools/SchoolLayout";
import ContactLink from "@/components/ContactLink";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { formatMoney, formatNumber, pluralize } from "@/lib/format";
import { cn } from "@/lib/utils";
import { useCopyToClipboard } from "@/lib/useCopyToClipboard";

export default function Payment({ school, membership, campaign, paid_plan, tiers, selected_tier, etransfer, update_path, back }) {
  const { errors } = usePage().props;
  const [selected, setSelected] = useState(selected_tier);
  useEffect(() => setSelected(selected_tier), [selected_tier]);
  const tier = tiers.find((t) => t.value === selected);

  const choose = (value) => {
    if (value === selected) return;
    setSelected(value);
    // A refused save (e.g. the roster outgrew the tier) snaps back to the
    // server's choice.
    router.patch(update_path, { requested_tier: value }, { preserveScroll: true, preserveState: "errors", onError: () => setSelected(selected_tier) });
  };

  return (
    <SchoolLayout
      school={school}
      membership={membership}
      eyebrow={campaign.name}
      title="Pay for your campaign"
      back={back}
    >
      <Head title={`${campaign.name} · Payment`} />

      <div className="max-w-3xl space-y-6">
        {campaign.approved ? (
          <Card>
            <CardContent className="flex items-start gap-3 p-6">
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-success" />
              <div>
                <p className="font-semibold text-foreground">This campaign is paid for and approved.</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  {paid_plan
                    ? `${paid_plan.label}, ${formatMoney(paid_plan.price_cents)} paid. `
                    : "Your school doesn't pay campaign fees. "}
                  You can connect Square, invite teachers and launch.
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <>
            <Card>
              <CardHeader>
                <CardTitle>1. Choose your plan</CardTitle>
                <CardDescription>
                  Your campaign has {pluralize(campaign.student_count, "student")}.
                  If more are still to come, choose a bigger plan now. Once it's approved, you can't add students past your plan's limit.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <fieldset className="grid gap-3 sm:grid-cols-3">
                  <legend className="sr-only">Plan</legend>
                  {tiers.map((t) => (
                    <TierOption key={t.value} tier={t} selected={t.value === selected} studentCount={campaign.student_count} onChoose={choose} />
                  ))}
                </fieldset>
                {errors?.base && <p role="alert" className="text-sm text-destructive">{errors.base}</p>}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>2. Pay by e-Transfer</CardTitle>
                <CardDescription>We approve your campaign once the payment arrives.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-baseline justify-between gap-4 rounded-xl border border-border bg-background px-5 py-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Total</p>
                    <p className="text-sm text-muted-foreground">{tier.name} plan · {campaign.name}</p>
                  </div>
                  <p className="font-display text-4xl tabular-nums text-foreground">
                    {formatMoney(tier.price_cents)} <span className="font-sans text-base text-muted-foreground">CAD</span>
                  </p>
                </div>

                <ol className="space-y-5">
                  <PayStep n={1} title={`Send an Interac e-Transfer for ${formatMoney(tier.price_cents)} to`}>
                    <CopyField value={etransfer.email} />
                  </PayStep>
                  <PayStep n={2} title="In the message, write">
                    <CopyField value={etransfer.message} />
                  </PayStep>
                  <PayStep n={3} title="We'll approve your campaign">
                    <p className="text-sm text-muted-foreground">
                      When the payment arrives, the trial banner goes away and you can connect Square, invite teachers and launch.
                      Questions? <ContactLink className="font-medium text-primary hover:underline underline-offset-4">Contact us</ContactLink>.
                    </p>
                  </PayStep>
                </ol>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </SchoolLayout>
  );
}

// A native radio (arrow keys, one tab stop) styled as a card.
function TierOption({ tier, selected, studentCount, onChoose }) {
  return (
    <label
      className={cn(
        "relative flex cursor-pointer flex-col rounded-xl border p-4 text-left transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring",
        selected ? "border-primary bg-primary/5 ring-1 ring-primary" : "border-border bg-card hover:border-primary/50",
        !tier.fits && "cursor-not-allowed opacity-50 hover:border-border"
      )}
    >
      <input
        type="radio"
        name="tier"
        value={tier.value}
        checked={selected}
        disabled={!tier.fits}
        onChange={() => onChoose(tier.value)}
        className="sr-only"
      />
      {selected && (
        <span aria-hidden="true" className="absolute right-3 top-3 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground">
          <Check className="h-3 w-3" strokeWidth={3} />
        </span>
      )}
      <span className="font-semibold text-foreground">{tier.name}</span>
      <span className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground">
        <Users className="h-3.5 w-3.5" aria-hidden="true" />
        {tier.student_limit ? `Up to ${formatNumber(tier.student_limit)} students` : "No student limit"}
      </span>
      <span className="mt-4 font-display text-2xl tabular-nums text-foreground">{formatMoney(tier.price_cents)}</span>
      {tier.standard_price_cents > tier.price_cents && (
        <span className="text-xs text-muted-foreground">
          Early adopter price, <span className="sr-only">standard price </span><del>{formatMoney(tier.standard_price_cents)}</del>
        </span>
      )}
      {!tier.fits && <span className="mt-2 text-xs text-muted-foreground">Too small for {pluralize(studentCount, "student")}</span>}
    </label>
  );
}

function PayStep({ n, title, children }) {
  return (
    <li className="flex gap-3">
      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">{n}</span>
      <div className="min-w-0 flex-1 space-y-2">
        <p className="text-sm font-medium text-foreground">{title}</p>
        {children}
      </div>
    </li>
  );
}

function CopyField({ value }) {
  const [copied, copy] = useCopyToClipboard();

  return (
    <div className="flex items-center gap-2">
      <code className="min-w-0 flex-1 truncate rounded-lg border border-border bg-background px-3 py-2 font-mono text-sm text-foreground select-all">
        {value}
      </code>
      <Button type="button" variant="outline" size="sm" onClick={() => copy(value)}>
        {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
        {copied ? "Copied" : "Copy"}
      </Button>
    </div>
  );
}
